
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, BookOpen, CalendarDays, ShoppingCart, Heart } from 'lucide-react';
import { cn, isMobileDevice } from '@/lib/utils';
import { useUser } from '@/contexts/UserContext';
import { useNavigationState } from '@/hooks/useNavigationState';
import { useModalContext } from '@/contexts/ModalProvider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const MobileNavigation: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useUser();
  const { canAccessMealPlanner, canAccessShoppingList, canAccessWellness } = useNavigationState();
  const { isAnyModalOpen } = useModalContext();
  
  const isActive = (path: string) => {
    // Special handling for Recipe Collection and all recipe-related pages
    if (path === '/recipe-collection') {
      return location.pathname === '/recipe-collection' ||
             location.pathname === '/recipes' ||
             location.pathname.startsWith('/recipe/') ||
             location.pathname.startsWith('/composition/') ||
             location.pathname === '/import-recipes' ||
             location.pathname === '/create-quick-recipe' ||
             location.pathname === '/create-recipe' ||
             location.pathname === '/recipe-finder' ||
             location.pathname === '/add-recipes' ||
             location.pathname === '/create-composition' ||
             location.pathname === '/compositions';
    }
    // Special handling for Meal Planner and all meal planning-related pages
    if (path === '/meal-planner') {
      return location.pathname === '/meal-planner' ||
             location.pathname.startsWith('/meal-selection');
    }
    // Special handling for homepage/overview
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path;
  };

  const primaryActions = [
    {
      title: "Overview",
      path: "/",
      icon: Home,
      enabled: true,
    },
    {
      title: "Recipes",
      path: "/recipe-collection",
      icon: BookOpen,
      enabled: true,
    },
    {
      title: "Meal Plan",
      path: "/meal-planner",
      icon: CalendarDays,
      enabled: canAccessMealPlanner,
      disabledTooltip: "Add recipes first to start meal planning",
    },
    {
      title: "Shopping",
      path: "/shopping-list",
      icon: ShoppingCart,
      enabled: canAccessShoppingList,
      disabledTooltip: "Plan meals first to generate shopping lists",
    },
    {
      title: "Wellness",
      path: "/wellness",
      icon: Heart,
      enabled: canAccessWellness,
      disabledTooltip: "Available after completing onboarding",
    },
  ];
  
  // Show navigation for all authenticated users (including new users)
  if (!isAuthenticated) {
    return null;
  }

  // Hide navigation when modal is open on mobile devices
  const shouldHideNavigation = isMobileDevice() && isAnyModalOpen;

  return (
    <>
      <div className={cn(
        "fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 lg:hidden transition-transform duration-300 ease-in-out",
        isMobileDevice() && [
          "bg-background/95",
          "border-t-0 shadow-[0_-1px_20px_rgba(0,0,0,0.08)]",
          "transform-gpu will-change-transform"
        ],
        shouldHideNavigation && "translate-y-full"
      )}>
        <TooltipProvider>
          <nav className={cn(
            "flex items-center justify-around py-2",
            isMobileDevice() && "py-2 pb-[env(safe-area-inset-bottom,8px)]"
          )}>
            {primaryActions.map((item) => {
              if (!item.enabled) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      <div className={cn(
                        "flex flex-col items-center justify-center p-2 text-xs font-medium min-w-0 flex-1 relative",
                        "text-muted-foreground opacity-50 cursor-not-allowed"
                      )}>
                        <div className={cn(
                          "p-2 rounded-xl",
                          "text-muted-foreground",
                          isMobileDevice() && "p-2.5 rounded-2xl"
                        )}>
                          <item.icon className="h-5 w-5" />
                        </div>
                        <span className={cn(
                          "truncate mt-1",
                          isMobileDevice() && "text-[11px] font-medium"
                        )}>{item.title}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{item.disabledTooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex flex-col items-center justify-center p-2 text-xs font-medium transition-colors hover:text-primary min-w-0 flex-1 relative",
                    isActive(item.path) ? "text-primary" : "text-muted-foreground"
                  )}
                >
                  <div className={cn(
                    "p-2 rounded-xl transition-all duration-200",
                    isActive(item.path) 
                      ? "bg-primary text-primary-foreground shadow-md" 
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                    isMobileDevice() && [
                      "p-2.5 rounded-2xl",
                      "active:scale-95 transition-transform duration-150",
                      isActive(item.path) && "shadow-lg"
                    ]
                  )}>
                    <item.icon className="h-5 w-5" />
                  </div>
                  <span className={cn(
                    "truncate mt-1",
                    isMobileDevice() && "text-[11px] font-medium"
                  )}>{item.title}</span>
                </Link>
              );
            })}
          </nav>
        </TooltipProvider>
      </div>
    </>
  );
};

export default MobileNavigation;
